import { Button, Heading } from "@r360/library";
import React, { CSSProperties, useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import AxiosClient from "../API/AxiosClient";
import PackingImage from "../assets/Packing.png";
import PageContainer from "../components/PageContainer";
import { RichText } from "../components/UI";
import { getDynamicPath } from "../Helper";
import useAppDispatch from "../hooks/useAppDispatch";
import useAppSelector from "../hooks/useAppSelector";
import useTranslate from "../hooks/useTranslate";
import { sendSignal } from "../signals";
import { createPurchaseSignal } from "../signals/purchaseSignal";
import * as types from "../store/actions/types";
import { TAccount } from "../store/types";

const CheckoutConfirmationPage = () => {
    const t = useTranslate();
    const dispatch = useAppDispatch();
    const { resvid } = useParams();
    const [searchParams] = useSearchParams();
    const paymentWasInvoice = searchParams.get("invoice") === "true";
    const verificationHash = searchParams.get("verificationHash");
    const account: TAccount = useAppSelector(state => state.account);
    const userToken = account.token;
    const [loadingReservation, setLoadingReservation] = useState(false);

    const fetchReservation = useCallback((userToken: string, resvid: string, verificationHash: string | null) => {
        if (userToken || verificationHash) {
            setLoadingReservation(true);

            let config = {},
                url = `/reservations/${resvid}`;

            if (userToken) {
                config = {
                    headers: {
                        Authorization: `Bearer ${userToken}`,
                    },
                };
            } else {
                // Unauthenticated users require a hash to fetch the reservation in a secure way.
                url += `?hash=${verificationHash}`;
            }

            AxiosClient.get(url, config).then(response => {
                setLoadingReservation(false);

                const purchaseCompletedSignal = createPurchaseSignal(response.data.payload, true);
                sendSignal(purchaseCompletedSignal);
            });
        }
    }, []);

    useEffect(() => {
        dispatch({ type: types.DESTROY_STATE });
        if (resvid) {
            fetchReservation(userToken, resvid, verificationHash);
        }
    }, [dispatch, fetchReservation, resvid, userToken, verificationHash]);

    const imgStyle: CSSProperties = {
        width: "90%",
        maxWidth: 600,
    };

    return (
        <PageContainer>
            <div className="u-d-flex u-flex-column u-align-items-center u-pt-72">
                <img
                    style={imgStyle}
                    className="u-mb-36"
                    src={getDynamicPath(PackingImage)}
                    alt="illustration of a person packing"
                />
                <Heading type="h2" styleAs="h3" className="u-mb-18 u-text-center">
                    <span className="u-color-success-dark">
                        {resvid && <RichText content={t("book.pay.confirmation.heading", [resvid])} />}
                    </span>
                    {` ${t("book.pay.confirmation.sub_heading")}`}
                </Heading>
                {account.user ? (
                    <p>
                        {paymentWasInvoice && `${t("book.pay.confirmation.invoice_will_be_sent")} `}
                        {`${t("book.pay.confirmation.view_booking")} `}
                        <Button
                            type="tertiary"
                            link
                            linkTo={`/my-pages/bookings/${resvid}`}
                            disabled={loadingReservation}
                        >
                            {t("book.my_pages")}
                        </Button>
                        .
                    </p>
                ) : (
                    <Button type="tertiary" link linkTo="/">
                        {t("book.general.go_to_startpage")}
                    </Button>
                )}
            </div>
        </PageContainer>
    );
};

export default CheckoutConfirmationPage;
